import { createFeature, createReducer, on } from '@ngrx/store';

import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import { Container } from 'app/shared/features/container-selector/models';
import { Locations } from 'app/shared/features/location-selector/models';
import { done, getInitialProgressState, ProgressState, running } from 'app/shared/models';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';

import {
    applyManualFlowRegistrationDataFromStorage,
    createRegistrationInManualFlow,
    initManualFlow,
    initManualFlowSuccess,
    resetManualFlowRegistrationData,
    selectActivityInManualFlow,
    setActivityInManualFlow,
    setAmountAndProductBoxInManualFlow,
    setAmountResultInManualFlow,
    setBatchInManualFlow,
    setContainerInManualFlow,
    setEmployeeInManualFlow,
    setLocationInManualFlow,
    setManualFlowComponentState,
    setProductBoxInManualFlow,
} from './manual-flow.actions';
import { ComponentState } from './select-state';

export const manualFlowFeatureKey = 'manualFlow';

export interface ManualFlowRegistrationData {
    employeeNumber: number | undefined;
    batchNumber: number | undefined;
    containers: Container[];
    amountResult: number | undefined;
    productBoxRegistrations: ProductBoxRegistration[];
    location: Locations | undefined;
}

export interface ManualFlowState {
    data: ManualFlowRegistrationData;
    initializationProgress: ProgressState;
    manualInputComponentState: ComponentState;
    activity: Activity | undefined;
}

export const emptyManualFlowRegistrationData: ManualFlowRegistrationData = {
    amountResult: undefined,
    batchNumber: undefined,
    employeeNumber: undefined,
    productBoxRegistrations: [],
    containers: [],
    location: undefined,
};

export const initialState: ManualFlowState = {
    data: emptyManualFlowRegistrationData,
    initializationProgress: getInitialProgressState(),
    manualInputComponentState: ComponentState.SelectActivity,
    activity: undefined,
};

export const reducer = createReducer(
    initialState,
    on(initManualFlow, (state) => {
        if (state.initializationProgress.done) return state;
        return { ...state, initializationProgress: running() };
    }),
    on(initManualFlowSuccess, (state) => ({ ...state, initializationProgress: done() })),
    on(resetManualFlowRegistrationData, resetApiConfig, (state, { resetBatch }) => ({
        ...state,
        data: {
            ...emptyManualFlowRegistrationData,
            batchNumber: resetBatch ? undefined : state.data.batchNumber,
        },
        activity: state.activity,
    })),
    on(applyManualFlowRegistrationDataFromStorage, (state, { registrationDataFromStorage }) => ({
        ...state,
        initializationProgress: done(),
        data: registrationDataFromStorage ?? emptyManualFlowRegistrationData,
    })),
    on(selectActivityInManualFlow, (state) => ({
        ...state,
        manualInputComponentState: ComponentState.SelectActivity,
    })),
    on(setActivityInManualFlow, (state, { activity }) => ({
        ...state,
        activity: activity,
    })),
    on(setBatchInManualFlow, (state, { batchNumber }) =>
        state.data.batchNumber === batchNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, batchNumber },
              },
    ),
    on(setManualFlowComponentState, (state, { componentState }) => ({
        ...state,
        manualInputComponentState: componentState,
    })),
    on(setEmployeeInManualFlow, (state, { employeeNumber }) =>
        state.data.employeeNumber === employeeNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, employeeNumber },
              },
    ),
    on(setLocationInManualFlow, (state, location) =>
        state.data.location === location
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      location: location,
                  },
              },
    ),
    on(setContainerInManualFlow, (state, { containerList }) =>
        state.data.containers === containerList
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      containers: containerList,
                  },
              },
    ),
    on(setAmountResultInManualFlow, (state, { amountResult }) =>
        state.data.amountResult === amountResult
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      amountResult: amountResult,
                  },
              },
    ),
    on(
        setAmountAndProductBoxInManualFlow,
        setProductBoxInManualFlow,
        (state, { updatedProductBoxRegistrations }) =>
            state.data.productBoxRegistrations === updatedProductBoxRegistrations
                ? state
                : {
                      ...state,
                      data: {
                          ...state.data,
                          productBoxRegistrations: updatedProductBoxRegistrations,
                      },
                  },
    ),
    on(createRegistrationInManualFlow, (state) => ({
        ...state,
        state: {
            state: ComponentState.CreateRegistration,
        },
    })),
);

export const manualFlowFeature = createFeature({
    name: manualFlowFeatureKey,
    reducer,
});
